import { DateRangeFormat } from "@intentsify/types";
import { formatDateRange, isNumeric } from "@intentsify/utils";
import { barY, crosshair, defineChart, lineY } from "@tanstack/charts";
import { scaleBand } from "@tanstack/charts/scales/band";
import { scaleLog, scaleUtc } from "d3-scale";
import { scaleLinear } from "@tanstack/charts/scales/linear";
import { DateTime } from "luxon";
import {
  TimeseriesItem,
  TimeseriesItemDataRecord,
  TimeseriesRange,
} from "types";
import { BrandChartTheme } from "../BrandChart";
import { formatTickValue } from "../Charts.utils";

export type TimeseriesRow = {
  itemIndex: number;
  label: string;
  dataKey: string;
  isoDate: string;
  time: number;
  date: Date;
  value: number | null;
  axis: "left" | "right";
};

type AnyTimeseriesItem<K extends string = string> = TimeseriesItem<
  K,
  TimeseriesItemDataRecord<string>
>;

/** Dash per data key, so two metrics of one series stay distinguishable. */
export const DATA_KEY_DASHES = [undefined, "6,4", "2,3", "10,3,2,3"];

export const dataKeyDash = (index: number) =>
  DATA_KEY_DASHES[index % DATA_KEY_DASHES.length];

const toTime = (isoDate: string) => DateTime.fromISO(isoDate).toUTC().valueOf();

/** Data keys holding numbers in at least one point, in first-seen order. */
export const getDataKeys = <K extends string>(data: AnyTimeseriesItem<K>[]) =>
  Array.from(
    new Set(
      data.flatMap((item) =>
        item.timeseries.flatMap(({ data: values }) =>
          Object.keys(values).filter((key) => isNumeric(values[key])),
        ),
      ),
    ),
  );

/**
 * X positions for every range start. With `startDate`, the axis is padded
 * backwards in steps of the range gap (or `defaultTimeGap` for a single
 * range) so a short series still shows the requested window.
 */
export const getTimeAxis = (
  timeseries: TimeseriesRange[],
  { startDate, defaultTimeGap }: { startDate?: Date; defaultTimeGap?: number },
) => {
  const times = timeseries.map((range) => toTime(range.start));
  const gap = times.length > 1 ? times[1] - times[0] : defaultTimeGap;

  if (startDate && times.length && typeof gap === "number" && gap > 0) {
    let previous = times[0] - gap;

    while (previous >= startDate.valueOf() && previous > 0) {
      times.unshift(previous);
      previous -= gap;
    }
  }

  return { times, gap };
};

/** Date-range label for an axis position, including padded positions. */
export const formatTimeLabel = (
  time: number,
  {
    timeseries,
    gap,
    dateFormat,
  }: {
    timeseries: TimeseriesRange[];
    gap?: number;
    dateFormat: DateRangeFormat;
  },
) => {
  const range = timeseries.find((r) => toTime(r.start) === time);

  if (range) {
    return formatDateRange(range, dateFormat);
  }

  if (typeof gap !== "number") {
    return "";
  }

  return formatDateRange(
    {
      start: DateTime.fromMillis(time).toUTC().toISO() ?? "",
      end:
        DateTime.fromMillis(time + gap - 1)
          .toUTC()
          .toISO() ?? "",
    },
    dateFormat,
  );
};

/**
 * One row per item, data key and date. Missing values plot as zero, as in the
 * legacy chart; on a log scale they break the line instead.
 */
export const toTimeseriesRows = <K extends string>(
  data: AnyTimeseriesItem<K>[],
  {
    dataMainKey,
    dataKeys,
    hiddenItems = [],
    hiddenDataKeys = [],
    useTwoYAxes = false,
    scale = "linear",
  }: {
    dataMainKey: K;
    dataKeys: string[];
    hiddenItems?: number[];
    hiddenDataKeys?: string[];
    useTwoYAxes?: boolean;
    scale?: "linear" | "log";
  },
): TimeseriesRow[] =>
  data.flatMap((item, itemIndex) => {
    if (hiddenItems.includes(itemIndex)) {
      return [];
    }

    const axis = useTwoYAxes && itemIndex === 1 ? "right" : "left";

    return dataKeys
      .filter((dataKey) => !hiddenDataKeys.includes(dataKey))
      .flatMap((dataKey) =>
        [...item.timeseries]
          .sort((a, b) => toTime(a.isoDate) - toTime(b.isoDate))
          .map((point) => {
            const raw = point.data[dataKey];
            const value = raw ? Number(raw) : 0;
            const time = toTime(point.isoDate);

            return {
              itemIndex,
              label: String(item[dataMainKey]),
              dataKey,
              isoDate: point.isoDate,
              time,
              date: new Date(time),
              value: scale === "log" && value <= 0 ? null : value,
              axis,
            };
          }),
      );
  });

const isPowerOfTen = (value: number) =>
  value > 0 && Number.isInteger(Math.log10(value));

const valueScale = (values: number[], scale: "linear" | "log") => {
  if (scale === "log") {
    const positive = values.filter((value) => value > 0);

    return scaleLog().domain(
      positive.length
        ? [Math.min(...positive), Math.max(...positive)]
        : [1, 10],
    );
  }

  return scaleLinear().domain([0, Math.max(0, ...values) || 1]);
};

const valueAxis = (
  scale: "linear" | "log",
  label: string | undefined,
  lineColor?: string,
) => ({
  line: lineColor ? { stroke: lineColor, strokeWidth: 2 } : false,
  label,
  ticks: {
    size: 0,
    format: (value: number) =>
      scale === "log" && !isPowerOfTen(value) ? "" : formatTickValue(value),
  },
});

type DefinitionOptions = {
  rows: TimeseriesRow[];
  times: number[];
  formatTime: (time: number) => string;
  brandTheme: BrandChartTheme;
  colors: string[];
  dataKeys: string[];
  scale: "linear" | "log";
  leftAxisLabel?: string;
  rightAxisLabel?: string;
  /** Series to keep at full strength; the rest are faded. */
  highlight?: { itemIndex?: number; dataKey?: string };
};

const isFaded = (
  highlight: DefinitionOptions["highlight"],
  itemIndex: number,
  dataKey: string,
) =>
  (highlight?.itemIndex !== undefined && highlight.itemIndex !== itemIndex) ||
  (highlight?.dataKey !== undefined && highlight.dataKey !== dataKey);

export const buildTimeseriesLinesDefinition = ({
  rows,
  times,
  formatTime,
  brandTheme,
  colors,
  dataKeys,
  scale,
  leftAxisLabel,
  rightAxisLabel,
  highlight,
}: DefinitionOptions) => {
  const series = new Map<string, TimeseriesRow[]>();

  rows.forEach((row) => {
    const key = `${row.itemIndex}:${row.dataKey}`;
    series.set(key, [...(series.get(key) ?? []), row]);
  });

  const valuesOn = (axis: TimeseriesRow["axis"]) =>
    rows
      .filter((row) => row.axis === axis)
      .map((row) => row.value)
      .filter((value): value is number => value !== null);

  const hasRightAxis = rows.some((row) => row.axis === "right");

  const lineFor = (seriesRows: TimeseriesRow[]) => {
    const { itemIndex, dataKey } = seriesRows[0];

    return {
      id: `series-${itemIndex}-${dataKey}`,
      x: "date",
      y: "value",
      key: (row: TimeseriesRow) =>
        `${row.itemIndex}:${row.dataKey}:${row.isoDate}`,
      stroke: colors[itemIndex % colors.length],
      strokeWidth: 2.5,
      strokeOpacity: isFaded(highlight, itemIndex, dataKey) ? 0.15 : 1,
      strokeDasharray: dataKeyDash(dataKeys.indexOf(dataKey)),
    } as const;
  };

  const allSeries = Array.from(series.values());

  return defineChart({
    theme: brandTheme.theme,
    marks: [
      ...allSeries
        .filter((seriesRows) => seriesRows[0].axis === "left")
        .map((seriesRows) => lineY(seriesRows, lineFor(seriesRows))),
      ...allSeries
        .filter((seriesRows) => seriesRows[0].axis === "right")
        .map((seriesRows) =>
          lineY(seriesRows, { ...lineFor(seriesRows), yScale: "right" }),
        ),
      crosshair({ x: true, y: false }),
    ],
    scales: {
      x: {
        scale: scaleUtc().domain([
          new Date(Math.min(...times)),
          new Date(Math.max(...times)),
        ]),
        axis: {
          ticks: {
            values: times.map((time) => new Date(time)),
            format: (value: Date) => formatTime(value.valueOf()),
          },
          tickLabels: { thin: true },
        },
      },
      y: {
        scale: valueScale(valuesOn("left"), scale),
        nice: scale === "linear",
        grid: true,
        axis: valueAxis(
          scale,
          leftAxisLabel,
          hasRightAxis ? colors[0] : undefined,
        ),
      },
      // Always declared so the definition has one shape; hidden when unused.
      right: {
        channel: "y",
        side: "right",
        scale: valueScale(valuesOn("right"), scale),
        nice: scale === "linear",
        axis: hasRightAxis
          ? valueAxis(scale, rightAxisLabel, colors[1 % colors.length])
          : false,
      },
    },
  });
};

/** Single-series bars, one per range (the legacy bars variant). */
export const buildTimeseriesBarsDefinition = ({
  rows,
  times,
  formatTime,
  brandTheme,
  colors,
  scale,
  leftAxisLabel,
}: DefinitionOptions) => {
  const values = rows
    .map((row) => row.value)
    .filter((value): value is number => value !== null);

  return defineChart({
    theme: brandTheme.theme,
    marks: [
      barY(rows, {
        x: "date",
        y: "value",
        key: "isoDate",
        fill: colors[0],
        radius: { end: 3 },
      }),
    ],
    scales: {
      x: {
        scale: scaleBand<Date>()
          .domain(times.map((time) => new Date(time)))
          .paddingInner(0.3)
          .paddingOuter(0.15),
        axis: {
          ticks: {
            size: 0,
            format: (value: Date) => formatTime(value.valueOf()),
          },
          tickLabels: { thin: true },
        },
      },
      y: {
        scale: valueScale(values, scale),
        nice: scale === "linear",
        grid: true,
        axis: valueAxis(scale, leftAxisLabel),
      },
    },
  });
};
